import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './FighterProfile.css';

// ── Debounce hook ─────────────────────────────────────────────────────────────
function useDebounce(value, delay) {
  const [dv, setDv] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDv(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return dv;
}

// ── Fighter autocomplete search ───────────────────────────────────────────────
function FighterSearch({ onSelect, placeholder, defaultName }) {
  const [query, setQuery] = useState(defaultName || '');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const suppressSearchRef = useRef(false);
  const dq = useDebounce(query, 220);
  const ref = useRef(null);

  // Sync search box text when defaultName changes (e.g., profile loaded from URL)
  useEffect(() => {
    if (defaultName) {
      suppressSearchRef.current = true;
      setQuery(defaultName);
    }
  }, [defaultName]);

  useEffect(() => {
    if (suppressSearchRef.current) { suppressSearchRef.current = false; return; }
    if (dq.length < 2) { setResults([]); return; }
    fetch(`/api/fighters/search?q=${encodeURIComponent(dq)}`, { credentials: 'include' })
      .then(r => r.json())
      .then(data => { setResults(data); setOpen(true); })
      .catch(() => {});
  }, [dq]);

  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const select = f => {
    setQuery(f.name);
    setOpen(false);
    setResults([]);
    onSelect(f);
  };

  const clear = () => {
    setQuery('');
    setResults([]);
    setOpen(false);
    onSelect(null);
  };

  return (
    <div className="fp-search-wrap" ref={ref}>
      <input
        className="fp-search-input"
        type="text"
        placeholder={placeholder || 'Search fighter…'}
        value={query}
        onChange={e => { setQuery(e.target.value); if (!e.target.value) onSelect(null); }}
        onFocus={() => results.length > 0 && setOpen(true)}
      />
      {query && <button className="fp-search-clear" onClick={clear}>×</button>}
      {open && results.length > 0 && (
        <div className="fp-search-dropdown">
          {results.map(f => (
            <div key={f.fighter_id} className="fp-search-result" onMouseDown={() => select(f)}>
              <span className="fp-res-name">{f.name}</span>
              <span className="fp-res-class">{f.weight_class || ''}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function fmtHeight(inches) {
  if (!inches) return '—';
  const ft = Math.floor(inches / 12);
  const ins = Math.round(inches % 12);
  return `${ft}'${ins}"`;
}

function fmtTime(sec) {
  if (!sec) return '—';
  return `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}`;
}

function fmtAvgTime(sec) {
  if (!sec) return '—';
  return `${Math.floor(sec / 60)}m ${sec % 60}s`;
}

function AmericanOdds({ val }) {
  if (val == null) return <span className="odds-dash">—</span>;
  const pos = val > 0;
  return <span className={`fp-odds-val ${pos ? 'fp-odds-pos' : 'fp-odds-neg'}`}>{pos ? `+${val}` : val}</span>;
}

function StatCell({ label, value, unit }) {
  return (
    <div className="fp-stat-cell">
      <div className="fp-stat-val">{value ?? '—'}{unit && value != null ? <span className="fp-stat-unit">{unit}</span> : ''}</div>
      <div className="fp-stat-label">{label}</div>
    </div>
  );
}

// ── Matchup / odds panel ──────────────────────────────────────────────────────
function MatchupPanel({ mainFighter }) {
  const [opponent, setOpponent] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!opponent) { setData(null); return; }
    setLoading(true);
    fetch(`/api/fight-odds?fighter_a_id=${mainFighter.fighter_id}&fighter_b_id=${opponent.fighter_id}`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [opponent, mainFighter]);

  // Group odds by bookmaker → { bookmaker: { fighter_id: odds_american } }
  const bookmakerMap = {};
  if (data?.odds) {
    for (const o of data.odds) {
      if (!bookmakerMap[o.bookmaker]) bookmakerMap[o.bookmaker] = {};
      bookmakerMap[o.bookmaker][o.fighter_id] = o.odds_american;
    }
  }
  const bookmakers = Object.keys(bookmakerMap);

  // Use fighter_id from API response (handles a/b ordering)
  const aId = data?.fighter_a?.fighter_id ?? mainFighter.fighter_id;
  const bId = data?.fighter_b?.fighter_id ?? opponent?.fighter_id;
  const aName = data?.fighter_a?.name ?? mainFighter.name;
  const bName = data?.fighter_b?.name ?? opponent?.name;

  const probA = data?.prob_a;
  const probB = data?.prob_b;
  const probMethod = data?.prob_method;

  return (
    <div className="fp-matchup-panel">
      <h3 className="fp-section-title">Matchup Odds</h3>
      <div className="fp-matchup-search-row">
        <span className="fp-matchup-vs">{mainFighter.name} vs.</span>
        <FighterSearch placeholder="Search opponent…" onSelect={setOpponent} />
      </div>

      {loading && <div className="fp-loading">Loading…</div>}

      {!loading && opponent && data && (
        <>
          {/* Model probability bar */}
          {probA != null && probB != null && (
            <div className="fp-prob-section">
              <div className="fp-prob-header">
                <span className="fp-prob-label">Model Win Probability</span>
                {probMethod && <span className="fp-prob-method">{probMethod}</span>}
              </div>
              <div className="fp-prob-bar-wrap">
                <span className="fp-prob-name fp-prob-a">{aName}</span>
                <div className="fp-prob-bar">
                  <div className="fp-prob-fill fp-prob-fill-a" style={{ width: `${probA}%` }} />
                  <div className="fp-prob-fill fp-prob-fill-b" style={{ width: `${probB}%` }} />
                </div>
                <span className="fp-prob-name fp-prob-b">{bName}</span>
              </div>
              <div className="fp-prob-pcts">
                <span className="fp-pct-a">{probA}%</span>
                <span className="fp-pct-b">{probB}%</span>
              </div>
            </div>
          )}

          {data.fight ? (
            <div className="fp-odds-section">
              <div className="fp-odds-event">
                <span className="fp-odds-event-name">{data.fight.event_name}</span>
                <span className="fp-odds-event-date">{data.fight.event_date}</span>
                {data.fight.is_title_fight && <span className="fp-title-badge">Title Fight</span>}
                <span className={`fp-status-badge fp-status-${data.fight.status}`}>{data.fight.status}</span>
              </div>

              {bookmakers.length > 0 ? (
                <table className="fp-odds-table">
                  <thead>
                    <tr>
                      <th>Bookmaker</th>
                      <th>{aName}</th>
                      <th>{bName}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bookmakers.map(bk => (
                      <tr key={bk}>
                        <td className="fp-bk-name">{bk}</td>
                        <td><AmericanOdds val={bookmakerMap[bk][aId]} /></td>
                        <td><AmericanOdds val={bookmakerMap[bk][bId]} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="fp-no-odds">Fight is scheduled but no odds recorded yet.</p>
              )}
            </div>
          ) : (
            <p className="fp-no-fight">
              No scheduled fight found — showing hypothetical probability only.
            </p>
          )}
        </>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function FighterProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [fighter, setFighter] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadById = async (fighterId) => {
    setProfile(null);
    setError(null);
    setLoading(true);
    try {
      const data = await fetch(`/api/fighter/${fighterId}/profile`, { credentials: 'include' }).then(r => r.json());
      if (data.error) throw new Error(data.error);
      setFighter({ fighter_id: data.fighter_id, name: data.name });
      setProfile(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) loadById(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const onSearchSelect = f => {
    if (!f) {
      setFighter(null);
      setProfile(null);
      navigate('/fighters');
      return;
    }
    navigate(`/fighters/${f.fighter_id}`);
  };

  const cs = profile?.career_stats;
  const rec = profile?.record;

  return (
    <div className="fp-page">
      <div className="fp-top-bar">
        <h2 className="fp-page-title">Fighter Profile</h2>
        <FighterSearch
          placeholder="Search fighter by name…"
          onSelect={onSearchSelect}
          defaultName={profile?.name || ''}
        />
      </div>

      {!fighter && (
        <div className="fp-empty">
          <p>Search for a fighter above to view their profile.</p>
        </div>
      )}

      {loading && <div className="fp-loading fp-loading-main">Loading profile…</div>}
      {error && <div className="fp-error">{error}</div>}

      {profile && (
        <>
          {/* ── Header ─────────────────────────────────────────────────── */}
          <div className="fp-header-card">
            <div className="fp-header-left">
              <h1 className="fp-name">{profile.name}</h1>
              {profile.nickname && <p className="fp-nickname">"{profile.nickname}"</p>}
              <div className="fp-badges">
                <span className="fp-badge fp-badge-class">{profile.weight_class || 'Unknown class'}</span>
                {profile.style && profile.style !== 'unknown' && (
                  <span className="fp-badge fp-badge-style">{profile.style.replace(/_/g, ' ')}</span>
                )}
                {!profile.is_active && <span className="fp-badge fp-badge-inactive">Inactive</span>}
              </div>
            </div>
            <div className="fp-header-right">
              <div className="fp-record">
                <span className="fp-record-wins">{rec.wins}</span>
                <span className="fp-record-sep">-</span>
                <span className="fp-record-losses">{rec.losses}</span>
                <span className="fp-record-sep">-</span>
                <span className="fp-record-draws">{rec.draws}</span>
              </div>
              <div className="fp-record-label">W – L – D</div>
            </div>
          </div>

          {/* ── Physical info ───────────────────────────────────────────── */}
          <div className="fp-info-row">
            {profile.height_inches && <div className="fp-info-item"><span className="fp-info-label">Height</span><span className="fp-info-val">{fmtHeight(profile.height_inches)}</span></div>}
            {profile.reach_inches && <div className="fp-info-item"><span className="fp-info-label">Reach</span><span className="fp-info-val">{profile.reach_inches}"</span></div>}
            {profile.age && <div className="fp-info-item"><span className="fp-info-label">Age</span><span className="fp-info-val">{profile.age}</span></div>}
            {profile.nationality && <div className="fp-info-item"><span className="fp-info-label">From</span><span className="fp-info-val">{profile.nationality}</span></div>}
          </div>

          {/* ── Ratings ─────────────────────────────────────────────────── */}
          <div className="fp-ratings-row">
            {profile.power_rating != null && (
              <div className="fp-rating-card">
                <div className="fp-rating-val">{profile.power_rating}</div>
                <div className="fp-rating-label">Power Rating</div>
              </div>
            )}
            {profile.elo != null && profile.elo !== 1500 && (
              <div className="fp-rating-card">
                <div className="fp-rating-val">{profile.elo}</div>
                <div className="fp-rating-label">Elo</div>
              </div>
            )}
            {profile.glicko2_rating != null && profile.glicko2_rating !== 1500 && (
              <div className="fp-rating-card">
                <div className="fp-rating-val">{profile.glicko2_rating}</div>
                <div className="fp-rating-label">Glicko-2 <span className="fp-rd">±{profile.glicko2_rd}</span></div>
              </div>
            )}
          </div>

          {/* ── Career stats ─────────────────────────────────────────────── */}
          {cs && (
            <div className="fp-section">
              <h3 className="fp-section-title">Career Averages</h3>
              <div className="fp-stats-grid">
                <StatCell label="Sig Str / Min" value={cs.sig_str_per_min} />
                <StatCell label="Sig Str Acc" value={cs.sig_str_acc} unit="%" />
                <StatCell label="TD / Min" value={cs.td_per_min} />
                <StatCell label="TD Acc" value={cs.td_acc} unit="%" />
                <StatCell label="Str Absorbed / Min" value={cs.str_abs_per_min} />
                <StatCell label="Total KDs" value={cs.total_kd} />
                <StatCell label="Sub Attempts" value={cs.total_sub_attempts} />
                <StatCell label="Avg Fight Time" value={cs.avg_fight_time_sec ? fmtAvgTime(cs.avg_fight_time_sec) : null} />
              </div>
            </div>
          )}

          {/* ── Fight history ────────────────────────────────────────────── */}
          {profile.fight_history?.length > 0 && (
            <div className="fp-section">
              <h3 className="fp-section-title">Fight History <span className="fp-fight-count">({profile.fight_history.length})</span></h3>
              <div className="fp-data-warning">
                ⚠ Fight history is sourced from the local database and may contain inaccuracies — opponent names can be wrong if the original data import mapped the wrong fighter profile. Verify against <a href="https://www.ufcstats.com" target="_blank" rel="noreferrer">ufcstats.com</a>.
              </div>
              <div className="fp-history-table-wrap">
                <table className="fp-history-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Opponent</th>
                      <th>Result</th>
                      <th>Method</th>
                      <th>Rnd</th>
                      <th>Time</th>
                      <th>Event</th>
                    </tr>
                  </thead>
                  <tbody>
                    {profile.fight_history.map((f, i) => (
                      <tr key={i}>
                        <td className="fp-hist-date">{f.date}</td>
                        <td className="fp-hist-opp">{f.opponent || '—'}</td>
                        <td>
                          <span className={`fp-result fp-result-${f.result}`}>
                            {f.result?.toUpperCase() || '—'}
                          </span>
                        </td>
                        <td className="fp-hist-method">{f.method || '—'}</td>
                        <td className="fp-hist-rnd">{f.round ?? '—'}</td>
                        <td className="fp-hist-time">{fmtTime(f.time_seconds)}</td>
                        <td className="fp-hist-event">{f.event || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── Matchup odds ─────────────────────────────────────────────── */}
          <MatchupPanel mainFighter={{ fighter_id: profile.fighter_id, name: profile.name }} />
        </>
      )}
    </div>
  );
}
